import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "template", "entry", "uploadTab", "manualTab", "uploadTabButton", "manualTabButton", "submitButton"]

  connect() {
    this.transcriptIndex = 0
    this.transcriptionStates = new Map()
    this.uploadsInProgress = 0
    this.findSubmitButton()
  }

  findSubmitButton() {
    const form = this.element.closest('form')
    if (form) {
      this.submitBtn = form.querySelector('input[type="submit"], button[type="submit"]')
    }
  }

  add() {
    const template = this.templateTarget.innerHTML
    this.transcriptIndex++
    const newEntry = template.replace(/NEW_INDEX/g, this.transcriptIndex)
    this.containerTarget.insertAdjacentHTML('beforeend', newEntry)
  }

  remove(event) {
    const entry = event.target.closest('.transcript-entry')
    if (this.containerTarget.querySelectorAll('.transcript-entry').length > 1) {
      const transcriptionEditor = entry.querySelector('[data-controller="transcription-editor"]')
      if (transcriptionEditor) {
        const token = transcriptionEditor.querySelector('[data-transcription-editor-target="hiddenToken"]')?.value
        if (token) {
          this.transcriptionStates.delete(token)
        }
      }
      entry.remove()
      this.updateSubmitButtonState()
    } else {
      const tokenInput = entry.querySelector('[data-transcription-editor-target="hiddenToken"]')
      const contentInput = entry.querySelector('[data-transcription-editor-target="hiddenContent"]')
      const manualContentInput = entry.querySelector('[id^="transcript_"][id$="_content"]')
      const sessionDateInputs = entry.querySelectorAll('input[type="date"]')

      if (tokenInput) {
        if (tokenInput.value) {
          this.transcriptionStates.delete(tokenInput.value)
        }
        tokenInput.value = ''
      }
      if (contentInput) contentInput.value = ''
      if (manualContentInput) manualContentInput.value = ''
      sessionDateInputs.forEach(input => input.value = '')

      const trixEditor = entry.querySelector('trix-editor')
      if (trixEditor && trixEditor.editor) {
        trixEditor.editor.loadHTML('')
      }

      this.updateSubmitButtonState()
    }
  }

  showUploadTab(event) {
    const entry = event.target.closest('.transcript-entry')
    if (!entry) return

    const uploadTab = entry.querySelector('.transcript-upload-tab')
    const manualTab = entry.querySelector('.transcript-manual-tab')
    const uploadButton = entry.querySelector('.transcript-tab-upload')
    const manualButton = entry.querySelector('.transcript-tab-manual')

    if (uploadTab && manualTab) {
      uploadTab.classList.remove('hidden')
      manualTab.classList.add('hidden')
    }

    if (uploadButton && manualButton) {
      uploadButton.classList.add('text-indigo-600', 'border-b-2', 'border-indigo-600')
      uploadButton.classList.remove('text-gray-500')
      manualButton.classList.remove('text-indigo-600', 'border-b-2', 'border-indigo-600')
      manualButton.classList.add('text-gray-500')
    }

    this.updateSubmitButtonState()
  }

  showManualTab(event) {
    const entry = event.target.closest('.transcript-entry')
    if (!entry) return

    const uploadTab = entry.querySelector('.transcript-upload-tab')
    const manualTab = entry.querySelector('.transcript-manual-tab')
    const uploadButton = entry.querySelector('.transcript-tab-upload')
    const manualButton = entry.querySelector('.transcript-tab-manual')

    if (uploadTab && manualTab) {
      uploadTab.classList.add('hidden')
      manualTab.classList.remove('hidden')
    }

    if (uploadButton && manualButton) {
      manualButton.classList.add('text-indigo-600', 'border-b-2', 'border-indigo-600')
      manualButton.classList.remove('text-gray-500')
      uploadButton.classList.remove('text-indigo-600', 'border-b-2', 'border-indigo-600')
      uploadButton.classList.add('text-gray-500')
    }

    this.updateSubmitButtonState()
  }

  handleTranscriptionReady(event) {
    const { ready, token, uploadInProgress, wasRemoved } = event.detail

    // Handle token-based tracking
    if (token) {
      if (wasRemoved) {
        this.transcriptionStates.delete(token)
      } else {
        // If this is the first time we're seeing this token, decrement the counter
        // (transitioning from counter-based to token-based tracking)
        if (!this.transcriptionStates.has(token) && this.uploadsInProgress > 0) {
          this.uploadsInProgress--
        }
        this.transcriptionStates.set(token, ready)
      }
    } else {
      // Track uploads without tokens by counter
      if (uploadInProgress) {
        this.uploadsInProgress++
      } else if (this.uploadsInProgress > 0) {
        // Upload removed before token was assigned
        this.uploadsInProgress--
      }
    }

    this.updateSubmitButtonState()
  }

  updateSubmitButtonState() {
    if (!this.submitBtn) {
      this.findSubmitButton()
    }
    if (!this.submitBtn) return

    // Check for uploads in progress without tokens
    if (this.uploadsInProgress > 0) {
      this.disableSubmitButton()
      return
    }

    const entries = this.containerTarget.querySelectorAll('.transcript-entry')
    let hasActiveUpload = false
    let hasInProgressTranscription = false

    entries.forEach(entry => {
      const uploadTab = entry.querySelector('.transcript-upload-tab')
      const isUploadTabActive = uploadTab && !uploadTab.classList.contains('hidden')

      if (isUploadTabActive) {
        const tokenInput = entry.querySelector('[data-transcription-editor-target="hiddenToken"]')
        const token = tokenInput?.value

        if (token) {
          hasActiveUpload = true
          const isReady = this.transcriptionStates.get(token)
          if (!isReady) {
            hasInProgressTranscription = true
          }
        }
      }
    })

    if (hasActiveUpload && hasInProgressTranscription) {
      this.disableSubmitButton()
    } else {
      this.enableSubmitButton()
    }
  }

  disableSubmitButton() {
    if (this.submitBtn) {
      this.submitBtn.disabled = true
      this.submitBtn.classList.add('opacity-50', 'cursor-not-allowed')
      this.submitBtn.classList.remove('hover:bg-indigo-700')
    }
  }

  enableSubmitButton() {
    if (this.submitBtn) {
      this.submitBtn.disabled = false
      this.submitBtn.classList.remove('opacity-50', 'cursor-not-allowed')
      this.submitBtn.classList.add('hover:bg-indigo-700')
    }
  }
}
